import React, { useState } from 'react';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import {
  Check,
  ChevronDown,
  Undo2,
  X,
  SkipForward,
  Play,
  Send,
  Loader2
} from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import {
  submitManagementTaskReview,
  reviewManagementTask,
  submitManagementSubtaskReview,
  reviewManagementSubtask,
  waiveManagementSubtask,
  updateManagementSubTask,
  updateManagementTask
} from '../../services/api/implementation/impl';
import DueDateReasonModal from './DueDateReasonModal';
import { TaskStatus } from './taskWorkflow';
import {
  canStartStatus,
  canSubmitStatus,
  isOpenMandatory,
  isWaived,
  trimmedNote
} from './taskTree';
import { actsAsOwner, isTaskReviewer } from './ownership';
import {
  COMPLETE_WITHOUT_APPROVAL_MESSAGE,
  usesPermissionFlow,
  PermissionStage,
  canStartPermissionSubtask,
  permissionStartBlockedMessage
} from './permission';

const CHIP =
  'inline-flex items-center justify-center gap-1 h-7 px-2.5 text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none transition-colors';

const TONES = {
  teal: 'text-[#346C75] hover:bg-[#346C75]/10',
  accept: 'text-emerald-700 hover:bg-emerald-50',
  return: 'text-amber-700 hover:bg-amber-50',
  reject: 'text-rose-700 hover:bg-rose-50',
  waive: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
};

function ActionChip({ tone = 'teal', icon: Icon, children, className = '', ...props }) {
  return (
    <button type="button" className={`${CHIP} rounded-md ${TONES[tone]} ${className}`} {...props}>
      {Icon ? <Icon size={13} strokeWidth={2.25} /> : null}
      {children}
    </button>
  );
}

const MENU_TONES = {
  accept: 'border-emerald-200 hover:bg-emerald-50 text-emerald-800',
  return: 'border-amber-200 hover:bg-amber-50 text-amber-800',
  reject: 'border-rose-200 hover:bg-rose-50 text-rose-800',
  waive: 'border-gray-200 hover:bg-gray-50 text-gray-700'
};

function MenuOption({ tone, icon: Icon, title, description, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`w-full flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors disabled:opacity-50 disabled:pointer-events-none ${MENU_TONES[tone]}`}
    >
      <Icon size={18} strokeWidth={2.25} className="mt-0.5 shrink-0" />
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block mt-0.5 text-xs text-gray-500 font-normal">{description}</span>
      </span>
    </button>
  );
}

const ReviewActions = ({
  task,
  subtask = null,
  onDone,
  showWaive = false,
  mode = 'all'
}) => {
  const userId = String(Cookies.get('userId') || '');
  const [busy, setBusy] = useState(false);
  const [reasonMode, setReasonMode] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  if (!task) return null;

  const status = subtask?.status || task.status;
  const assigneeId = subtask ? subtask.assignedTo_id : task.assignedTo_id;
  const isAssignee = String(assigneeId || '') === userId;
  // The owner — or the company's builder acting as owner — reviews what has no
  // approver, and alone may waive.
  const isReviewer = isTaskReviewer(task, subtask);
  const isOwner = actsAsOwner(task);
  const isParentCard = !subtask;

  const waived = isWaived(subtask);
  const typeStartAllowed = canStartPermissionSubtask(task, subtask);
  const typeStartBlockedMessage = permissionStartBlockedMessage(subtask?.permissionType);
  const showStart =
    !isParentCard &&
    !waived &&
    (mode === 'all' || mode === 'submit') &&
    isAssignee &&
    canStartStatus(status);
  const startBlocked = showStart && !typeStartAllowed;
  const showSubmit =
    !isParentCard &&
    !waived &&
    (mode === 'all' || mode === 'submit') &&
    isAssignee &&
    canSubmitStatus(status);
  const showReview =
    !isParentCard &&
    !waived &&
    (mode === 'all' || mode === 'review') &&
    isReviewer &&
    status === TaskStatus.UNDER_REVIEW;
  const showWaiveBtn =
    showWaive && isOwner && !!subtask && isOpenMandatory(subtask);
  const showMenu = showReview || showWaiveBtn;

  if (!showStart && !showSubmit && !showMenu) return null;

  const pickMenuAction = (key) => {
    setMenuOpen(false);
    if (key === 'accept') {
      review('accept');
      return;
    }
    if (key === 'return') setReasonMode('return');
    if (key === 'reject') setReasonMode('reject');
    if (key === 'waive') setReasonMode('waive');
  };

  const run = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      onDone?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const start = () => {
    if (subtask && !canStartPermissionSubtask(task, subtask)) {
      toast.error(permissionStartBlockedMessage(subtask.permissionType));
      return;
    }
    if (subtask) {
      return run(
        () => updateManagementSubTask(subtask.id, { status: TaskStatus.IN_PROGRESS }),
        'Work started'
      );
    }
    return run(
      () => updateManagementTask(task.id, { status: TaskStatus.IN_PROGRESS }),
      'Work started'
    );
  };

  const submit = (reason) => {
    if (subtask) {
      return run(
        () => submitManagementSubtaskReview(subtask.id, reason),
        'Submitted for review'
      );
    }
    return run(() => submitManagementTaskReview(task.id, reason), 'Submitted for review');
  };

  const review = (action, reason) => {
    if (
      action === 'accept' &&
      !subtask &&
      usesPermissionFlow(task) &&
      task.permissionStage !== PermissionStage.APPROVAL
    ) {
      if (!window.confirm(COMPLETE_WITHOUT_APPROVAL_MESSAGE)) return;
    }
    const payload = { action, reason };
    const label =
      action === 'accept' ? 'Accepted' : action === 'return' ? 'Returned' : 'Rejected';
    if (subtask) {
      return run(() => reviewManagementSubtask(subtask.id, payload), label);
    }
    return run(() => reviewManagementTask(task.id, payload), label);
  };

  const waive = (reason) =>
    run(() => waiveManagementSubtask(subtask.id, reason), 'Subtask waived');

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        {busy && <Loader2 size={14} className="animate-spin text-gray-400 shrink-0" />}
        {showStart && (
          <ActionChip
            icon={Play}
            disabled={busy}
            title={startBlocked ? typeStartBlockedMessage : 'Start work'}
            className={startBlocked ? 'opacity-50 cursor-not-allowed' : ''}
            onClick={() => {
              if (startBlocked) {
                toast.error(typeStartBlockedMessage);
                return;
              }
              start();
            }}
          >
            Start
          </ActionChip>
        )}
        {showSubmit && (
          <ActionChip
            icon={Send}
            disabled={busy}
            title={trimmedNote(subtask?.returnReason) ? 'Resubmit for review' : 'Submit for review'}
            onClick={() => setReasonMode('submit')}
          >
            {trimmedNote(subtask?.returnReason) ? 'Resubmit' : 'Submit'}
          </ActionChip>
        )}
        {showMenu && (
          <ActionChip
            disabled={busy}
            title="Review actions"
            onClick={() => setMenuOpen(true)}
          >
            Actions
            <ChevronDown size={13} strokeWidth={2.25} />
          </ActionChip>
        )}
      </div>
      {menuOpen && (
        <Modal name="Choose an action" width={34} onClose={() => setMenuOpen(false)}>
          <div className="flex flex-col gap-2">
            {showReview && (
              <>
                <MenuOption
                  tone="accept"
                  icon={Check}
                  title="Accept"
                  description="Mark this subtask as completed."
                  disabled={busy}
                  onClick={() => pickMenuAction('accept')}
                />
                <MenuOption
                  tone="return"
                  icon={Undo2}
                  title="Return"
                  description="Send it back to the assignee with a reason."
                  disabled={busy}
                  onClick={() => pickMenuAction('return')}
                />
                <MenuOption
                  tone="reject"
                  icon={X}
                  title="Reject"
                  description="Reject this submission with a reason."
                  disabled={busy}
                  onClick={() => pickMenuAction('reject')}
                />
              </>
            )}
            {showWaiveBtn && (
              <MenuOption
                tone="waive"
                icon={SkipForward}
                title="Waive"
                description="Skip this mandatory subtask so the parent can be accepted."
                disabled={busy}
                onClick={() => pickMenuAction('waive')}
              />
            )}
          </div>
        </Modal>
      )}
      {reasonMode === 'submit' && (
        <DueDateReasonModal
          title={trimmedNote(subtask?.returnReason) ? 'Resubmit for review' : 'Submit for review'}
          body="Tell the approver what to check. After a return, describe what you fixed."
          helper={
            trimmedNote(subtask?.returnReason)
              ? `Previously returned: ${trimmedNote(subtask.returnReason)}`
              : undefined
          }
          confirmLabel={trimmedNote(subtask?.returnReason) ? 'Resubmit' : 'Submit'}
          onClose={() => setReasonMode(null)}
          onConfirm={(reason) => {
            setReasonMode(null);
            submit(reason);
          }}
        />
      )}
      {reasonMode === 'return' && (
        <DueDateReasonModal
          title="Return task"
          body="Why is this being sent back to the assignee?"
          confirmLabel="Return"
          onClose={() => setReasonMode(null)}
          onConfirm={(reason) => {
            setReasonMode(null);
            review('return', reason);
          }}
        />
      )}
      {reasonMode === 'reject' && (
        <DueDateReasonModal
          title="Reject task"
          body="Enter a reason to reject this work."
          confirmLabel="Reject"
          onClose={() => setReasonMode(null)}
          onConfirm={(reason) => {
            setReasonMode(null);
            review('reject', reason);
          }}
        />
      )}
      {reasonMode === 'waive' && (
        <DueDateReasonModal
          title="Waive subtask"
          body="Enter a reason to waive this mandatory subtask so the parent can be accepted."
          confirmLabel="Waive"
          onClose={() => setReasonMode(null)}
          onConfirm={(reason) => {
            setReasonMode(null);
            waive(reason);
          }}
        />
      )}
    </>
  );
};

export default ReviewActions;
